import { NonEmptyArray } from 'fp-ts/NonEmptyArray';
import { lt, createExperimentalZodFormResolver } from '@mews/framework';
import { TabbedDialogFormProps, useTabbedDialogForm, useTabbedDialogFormControls } from '@mews-commander/form';
import { OptimusProvider, OverlayedLoader } from '@mews-commander/ui';
import { WithProductId } from '@mews-commander/types';
import { useRepositorySuspenseQuery } from '@mews-commander/data-access';
import { useCurrentService } from '../queries/useCurrentService';
import { AllowanceFormStep } from './types';
import { DetailsStepFields } from './steps/DetailsStepFields';
import { allowanceAccountingSchema } from './schemas/accountingSchema';
import { useDetailsSchema } from './schemas/detailsSchema';
import { allowancePricingSchema } from './schemas/pricingSchema';
import { allowanceOptionsSchema } from './schemas/optionsSchema';
import {
    AllowanceFormValues,
    useAllowanceFormSchema,
    useToAllowanceEditFormDefaultValues,
} from './schemas/allowanceFormSchema';
import { AllowancePricingStepFields } from './steps/AllowancePricingStepFields';
import { AllowanceOptionsStepFields } from './steps/AllowanceOptionsStepFields';
import { AllowanceAccountingStepFields } from './steps/AllowanceAccountingStepFields';

const ALLOWANCE_FORM_TABS: NonEmptyArray<AllowanceFormStep> = ['details', 'pricing', 'options', 'accounting'];

export const useEditAllowanceDialogForm = ({
    initialTab = 'details',
    productId,
}: WithProductId & { initialTab?: AllowanceFormStep }) => {
    const detailsSchema = useDetailsSchema();

    return useTabbedDialogForm<AllowanceFormStep, AllowanceFormValues>({
        id: 'editAllowanceForm',
        initialTab,
        title: lt('EditAllowance'),
        tabs: ALLOWANCE_FORM_TABS,
        tabLabels: {
            details: lt('DetailPlural'),
            pricing: lt('Pricing'),
            options: lt('Options'),
            accounting: lt('Accounting'),
        },
        tabFields: {
            details: detailsSchema.keyof().options,
            pricing: allowancePricingSchema.keyof().options,
            options: allowanceOptionsSchema.keyof().options,
            accounting: allowanceAccountingSchema.keyof().options,
        },
        renderForm: (props) => (
            <OptimusProvider density="spacious">
                <AllowanceForm productId={productId} {...props} />
            </OptimusProvider>
        ),
    });
};

const AllowanceForm = ({ productId, ...other }: WithProductId & TabbedDialogFormProps<AllowanceFormStep>) => {
    const { id: serviceId } = useCurrentService();

    const { data, isFetching } = useRepositorySuspenseQuery('allowances', 'get', {
        serviceId,
        allowanceProductId: productId,
    });

    const allowanceFormSchema = useAllowanceFormSchema();

    const toAllowanceEditFormDefaultValues = useToAllowanceEditFormDefaultValues();

    const { renderFormContent } = useTabbedDialogFormControls<AllowanceFormStep, AllowanceFormValues>({
        ...other,
        values: toAllowanceEditFormDefaultValues(data),
        resolver: createExperimentalZodFormResolver(allowanceFormSchema),
        onSubmit: () => {}, // TO BE DONE
    });

    return renderFormContent(({ matchTabs }) => (
        <>
            <OverlayedLoader show={isFetching} />
            {matchTabs({
                details: () => (
                    <DetailsStepFields
                        enabledUnits={false}
                        helpers={{
                            descriptions: lt('AllowanceFormInputHintDescription'),
                            image: lt('AllowanceFormInputHintImage'),
                            externalIdentifier: lt('AllowanceFormInputHintExternalIdentifier'),
                            names: lt('AllowanceFormInputHintName'),
                        }}
                    />
                ),
                pricing: () => <AllowancePricingStepFields />,
                options: () => <AllowanceOptionsStepFields />,
                accounting: () => <AllowanceAccountingStepFields />,
            })}
        </>
    ));
};
